/**
 * ابزارهای خواندن JSON تحلیل مکالمه (قالب سفارشی تعریف‌شده در پرامپت).
 * بخش‌ها بر اساس نام کلید شناسایی می‌شوند تا تغییر جزئی ساختار یا سطح تودرتویی، نمایش را خراب نکند.
 */

export const CONVERSATION_STAGES = [
  'شروع مکالمه',
  'ارتباط‌سازی',
  'نیازسنجی',
  'معرفی محصول',
  'ارائه راهکار',
  'مدیریت اعتراض',
  'مذاکره',
  'Closing',
  'تعیین اقدام بعدی',
  'پایان تماس',
];

/** برچسب فارسی کلیدهای رایج؛ کلیدهای ناشناخته با humanizeKey نمایش داده می‌شوند */
const KEY_LABELS = {
  call_info: 'اطلاعات تماس',
  basic_info: 'اطلاعات پایه تماس',
  topic: 'موضوع تماس',
  call_topic: 'موضوع تماس',
  subject: 'موضوع تماس',
  customer_need: 'نیاز مشتری',
  customer_needs: 'نیاز مشتری',
  need: 'نیاز مشتری',
  objections: 'اعتراض‌ها',
  customer_objections: 'اعتراض‌ها',
  product: 'محصول موردنظر',
  product_of_interest: 'محصول موردنظر',
  requested_product: 'محصول موردنظر',
  budget: 'بودجه / محدودیت',
  budget_constraints: 'بودجه / محدودیت',
  constraints: 'بودجه / محدودیت',
  budget_or_constraints: 'بودجه / محدودیت',
  customer_decision: 'تصمیم مشتری',
  decision: 'تصمیم مشتری',
  agent_commitment: 'تعهد اپراتور',
  operator_commitment: 'تعهد اپراتور',
  agent_commitments: 'تعهد اپراتور',
  next_action: 'اقدام بعدی',
  next_step: 'اقدام بعدی',
  next_steps: 'اقدام بعدی',
  summary: 'خلاصه',
  stages: 'مراحل مکالمه',
  conversation_stages: 'مراحل مکالمه',
  current_stage: 'مرحله غالب',
  sales_process_findings: 'یافته‌های فرایند فروش',
  talk_ratio: 'نسبت زمان صحبت',
  talk_time: 'نسبت زمان صحبت',
  measurement_basis: 'مبنای محاسبه',
  interruptions: 'قطع صحبت مشتری',
  voice_delivery: 'نحوه بیان اپراتور',
  voice_delivery_analysis: 'نحوه بیان اپراتور',
  coaching: 'پیشنهادهای Coaching',
  coaching_recommendations: 'پیشنهادهای Coaching',
  recommendations: 'پیشنهادها',
  strengths: 'نقاط قوت',
  weaknesses: 'نقاط قابل بهبود',
  improvements: 'نقاط قابل بهبود',
  improvement_areas: 'نقاط قابل بهبود',
  key_moments: 'لحظات کلیدی',
  risks: 'ریسک‌ها',
  opportunities: 'فرصت‌ها',
  overall_score: 'امتیاز کلی',
  score: 'امتیاز',
  quality_score: 'امتیاز کیفیت',
  crm_insights: 'تحلیل تکمیلی CRM',
  coaching_description: 'توضیح Coaching',
  limitations: 'محدودیت‌های تحلیل',
  analysis_limitations: 'محدودیت‌های تحلیل',
  calmness: 'آرامش',
  confidence: 'اطمینان',
  haste: 'عجله',
  hurry: 'عجله',
  rush: 'عجله',
  rushing: 'عجله',
  monotony: 'یکنواختی',
  monotone: 'یکنواختی',
  energy: 'انرژی',
  respect: 'احترام',
  politeness: 'احترام',
  assessment: 'ارزیابی',
  evidence: 'شواهد',
  explanation: 'توضیح',
  reason: 'علت',
  start_seconds: 'شروع (ثانیه)',
  end_seconds: 'پایان (ثانیه)',
  timestamp_seconds: 'زمان (ثانیه)',
  customer_quote: 'گفته مشتری',
  agent_quote: 'گفته اپراتور',
};

/** شاخص‌هایی که مقدار «بالا»ی آن‌ها نامطلوب است (رنگ‌بندی معکوس) */
const INVERTED_INDICATORS = /haste|hurry|rush|monoton|عجله|یکنواخت/i;

export const labelFor = (key) => KEY_LABELS[key] || humanizeKey(key);

export const humanizeKey = (key) => String(key).replace(/[_-]+/g, ' ').trim();

export const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

/** «ندارد»، رشته خالی، null و آرایه خالی = بدون اطلاعات */
export const isEmptyValue = (value) => value === null
  || value === undefined
  || (typeof value === 'string' && ['', 'ندارد', '-', '—'].includes(value.trim()))
  || (Array.isArray(value) && value.length === 0);

const normalizeText = (value) => String(value ?? '').replace(/[‌\s]/g, '').toLowerCase();

export const isSameStage = (a, b) => normalizeText(a) === normalizeText(b);

export const toFaNumber = (value, options) => (typeof value === 'number' && Number.isFinite(value)
  ? value.toLocaleString('fa-IR', options)
  : '—');

export const formatSeconds = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const total = Math.max(0, Math.round(value));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

/** رنگ ارزیابی متنی (فارسی یا انگلیسی)؛ برای شاخص‌های منفی مثل عجله معکوس می‌شود */
export const assessmentTone = (assessment, indicatorKey = '') => {
  const text = normalizeText(assessment);
  if (!text || text === 'ندارد' || text === 'null') return 'default';

  const good = /(عالی|خوب|مناسب|مثبت|بالا|زیاد|قوی|کامل|excellent|good|high|strong|positive|appropriate)/;
  const bad = /(ضعیف|نامناسب|منفی|پایین|کم|بد|poor|low|weak|negative|bad|inappropriate)/;
  const mid = /(متوسط|نسبی|قابلقبول|medium|moderate|average|fair)/;

  let tone = 'processing';
  if (mid.test(text)) tone = 'warning';
  else if (bad.test(text)) tone = 'error';
  else if (good.test(text)) tone = 'success';

  if (INVERTED_INDICATORS.test(indicatorKey) && (tone === 'success' || tone === 'error')) {
    return tone === 'success' ? 'error' : 'success';
  }
  return tone;
};

/** پیمایش سطح‌به‌سطح؛ هر ورودی شامل کلید، مقدار، کلید سطح اول و عمق است */
const walk = (data, maxDepth = 5) => {
  const entries = [];
  const queue = Object.entries(data).map(([key, value]) => ({ key, value, topKey: key, depth: 1 }));

  while (queue.length) {
    const entry = queue.shift();
    entries.push(entry);
    if (entry.depth < maxDepth && isPlainObject(entry.value)) {
      Object.entries(entry.value).forEach(([key, value]) => {
        queue.push({ key, value, topKey: entry.topKey, depth: entry.depth + 1 });
      });
    }
  }
  return entries;
};

const firstNumber = (obj, keys) => {
  const key = keys.find((k) => typeof obj?.[k] === 'number');
  return key ? obj[key] : null;
};

const firstString = (obj, pattern, exclude = []) => {
  const entry = Object.entries(obj || {}).find(([key, value]) => pattern.test(key)
    && !exclude.includes(key)
    && typeof value === 'string'
    && !isEmptyValue(value));
  return entry ? entry[1] : null;
};

/**
 * تبدیل JSON خام به مدل نمایشی بخش‌بندی‌شده.
 */
export const parseCallAnalysis = (data) => {
  if (!isPlainObject(data)) return null;

  const entries = walk(data);
  const findEntry = (predicate) => entries.find(predicate) || null;
  const consumed = new Set();
  const use = (entry) => {
    if (entry) consumed.add(entry.topKey);
    return entry;
  };

  // ۱. اطلاعات پایه: شیئی که کلید summary دارد (یا خود ریشه)
  const infoEntry = use(findEntry(({ value }) => isPlainObject(value) && typeof value.summary === 'string'));
  const infoSource = infoEntry ? infoEntry.value : (typeof data.summary === 'string' ? data : null);
  const info = infoSource
    ? Object.entries(infoSource)
      .filter(([key, value]) => key !== 'summary' && (typeof value === 'string' || (Array.isArray(value) && value.every((v) => typeof v === 'string'))))
      .map(([key, value]) => ({ key, label: labelFor(key), value: Array.isArray(value) ? value.join('، ') : value }))
    : [];
  if (!infoEntry && infoSource) consumed.add('summary');

  // ۲. مراحل مکالمه
  const stagesEntry = use(findEntry(({ key, value }) => /stages?$/i.test(key) && Array.isArray(value)));
  const stages = (stagesEntry?.value || [])
    .map((item) => (typeof item === 'string'
      ? { name: item }
      : {
        name: item?.stage ?? item?.name ?? item?.title ?? '',
        start: firstNumber(item, ['start_seconds', 'start', 'start_time', 'start_timestamp_seconds']),
        end: firstNumber(item, ['end_seconds', 'end', 'end_time', 'end_timestamp_seconds']),
        note: firstString(item, /note|description|evidence|reason|explanation/i),
      }))
    .filter((stage) => !isEmptyValue(stage.name));
  const currentStageEntry = use(findEntry(({ key, value }) => key === 'current_stage' && typeof value === 'string'));
  const findingsEntry = use(findEntry(({ key, value }) => /finding/i.test(key) && Array.isArray(value)));

  // ۳. نسبت زمان صحبت
  const talkEntry = use(findEntry(({ value }) => isPlainObject(value)
    && ['agent_percent', 'customer_percent', 'agent_speaking_seconds'].some((k) => k in value)));
  const talk = talkEntry ? {
    agentPercent: firstNumber(talkEntry.value, ['agent_percent']),
    customerPercent: firstNumber(talkEntry.value, ['customer_percent']),
    agentSeconds: firstNumber(talkEntry.value, ['agent_speaking_seconds', 'agent_seconds']),
    customerSeconds: firstNumber(talkEntry.value, ['customer_speaking_seconds', 'customer_seconds']),
    basis: firstString(talkEntry.value, /basis|note|limitation|method/i),
  } : null;

  // ۴. قطع صحبت مشتری
  const interruptEntry = use(findEntry(({ key }) => /interrupt/i.test(key)));
  let interruptions = null;
  if (interruptEntry) {
    const raw = interruptEntry.value;
    const items = Array.isArray(raw)
      ? raw
      : Object.values(raw || {}).find((v) => Array.isArray(v) && v.some(isPlainObject)) || [];
    interruptions = {
      count: isPlainObject(raw) ? firstNumber(raw, ['count', 'total']) : items.length,
      note: isPlainObject(raw) ? firstString(raw, /basis|note|limitation|reason|explanation|method/i) : null,
      items: items.filter(isPlainObject).map((item) => ({
        time: firstNumber(item, ['timestamp_seconds', 'time_seconds', 'timestamp', 'start_seconds']),
        customerQuote: firstString(item, /customer/i),
        agentQuote: firstString(item, /agent|operator/i),
        reason: firstString(item, /reason|explanation|description/i),
        confidence: firstNumber(item, ['confidence']),
      })),
    };
  }

  // ۵. نحوه بیان اپراتور
  const voiceEntry = use(findEntry(({ key, value }) => /voice|delivery/i.test(key) && isPlainObject(value)));
  let voice = null;
  if (voiceEntry) {
    const raw = voiceEntry.value;
    const container = Object.values(raw).find((v) => isPlainObject(v) && Object.values(v).some((x) => isPlainObject(x) && 'assessment' in x)) || raw;
    const indicators = Object.entries(container)
      .filter(([, value]) => isPlainObject(value) && ('assessment' in value || 'score' in value))
      .map(([key, value]) => ({
        key,
        label: labelFor(key),
        assessment: value.assessment ?? null,
        score: firstNumber(value, ['score', 'rating']),
        confidence: firstNumber(value, ['confidence']),
        evidence: firstString(value, /evidence|explanation|reason|description|note/i),
      }));
    voice = {
      indicators,
      note: firstString(raw, /basis|note|limitation|reason|explanation|method|summary/i),
    };
  }

  // تحلیل تکمیلی CRM (موضوع، اعتراض، ریسک، کیفیت) در بخش اختصاصی خودش نمایش داده می‌شود
  use(findEntry(({ key }) => key === 'crm_insights'));

  const otherSections = Object.entries(data)
    .filter(([key]) => !consumed.has(key))
    .map(([key, value]) => ({ key, label: labelFor(key), value }));

  return {
    summary: infoSource?.summary ?? null,
    info,
    stages,
    currentStage: currentStageEntry?.value ?? null,
    findings: (findingsEntry?.value || []).filter((f) => !isEmptyValue(f)),
    talk,
    interruptions,
    voice,
    otherSections,
    highlights: {
      decision: firstString(infoSource, /decision/i),
      nextAction: firstString(infoSource, /next/i),
      commitment: firstString(infoSource, /commit/i),
    },
  };
};
